// @flow

import React, { Fragment } from 'react';
import { Spacer } from '@zapier/design-system';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import { getUserFields } from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

export const createCantSeeLeads = (isLoggedIn: boolean) => ({
  value: 'cant-see-leads',
  label: "I can't see my submitted leads and/or clients",
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Submmitted leads',
      body: () => (
        <Fragment>
          <p>
            You can track referral leads in your Partner Portal under the Leads
            tab or by status in Opportunities. MRR submissions are not visible
            in the portal just yet but stay tuned.
          </p>

          <Spacer height={16} />

          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href: 'https://partnerportal.zapier.com/lead/index/',
          title: 'Leads in Partner Portal',
        },
        {
          href: 'https://partnerportal.zapier.com/opportunity/index/',
          title: 'Opportunities in Partner Portal',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'lead_email',
        type: 'text',
        label:
          'Provide the email address of the lead you want to enquire about',
        isRequired: false,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-reporting',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382904444941',
        type: 'hidden',
        value: 'expsup-expert-reporting-lead',
        zendeskCustomFieldId: 33382904444941,
      },
    ],
  }),
});

export const createHowToSubmit = (isLoggedIn: boolean) => ({
  value: 'how-to-submit',
  label: 'How to submit leads and/or clients',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Submitting leads',
      body: () => (
        <Fragment>
          <p>You can register referral leads in two ways:</p>

          <Spacer height={16} />

          <p style={{ fontWeight: 'bold' }}>
            Partner Portal – Use the built-in deal registration form
          </p>
          <p>
            You can access this page by clicking on the "New Deal Registration"
            that appears under your name when you log in to the portal.
          </p>

          <Spacer height={16} />

          <p style={{ fontWeight: 'bold' }}>
            Webhook – Automate lead submission directly from your systems
          </p>
          <p>
            One of the fastest and most efficient ways to submit referral leads
            is by using a webhook. This method allows you to automatically send
            lead information directly to Zapier from your system in real time.
            It's ideal for partners who want to streamline their workflow and
            ensure no leads are missed. Whether you're capturing leads through a
            form, CRM, or internal tool, integrating a webhook enables seamless
            and secure data transfer without manual effort.
          </p>

          <Spacer height={16} />

          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href:
            'https://partnerportal.zapier.com/wikis/help/RegisteringNewDeals',
          title: 'Registering New Deals',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'lead_email',
        type: 'text',
        label:
          'Provide the email address of the lead you want to enquire about',
        isRequired: false,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-reporting',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382904444941',
        type: 'hidden',
        value: 'expsup-expert-reporting-lead',
        zendeskCustomFieldId: 33382904444941,
      },
    ],
  }),
});

export const createLeadRejected = (isLoggedIn: boolean) => ({
  value: 'lead-rejected',
  label: 'My lead was rejected',
  child: help({
    skipInterstitial: true,
    docs: {
      title: 'Rejected leads',
      body: () => (
        <Fragment>
          <p>Leads can be rejected for a multitude of reasons.</p>

          <Spacer height={16} />

          <p>To avoid this:</p>
          <ul style={{ listStyle: 'disc', paddingLeft: '20px' }}>
            <li>
              Submit early, before the lead is contacted by Zapier Sales or
              signs up directly
            </li>
            <li>
              Check your Partner Portal regularly for submission status and
              errors
            </li>
            <li>
              Use proper formatting and avoid trailing spaces or tabs in field
              values
            </li>
          </ul>
          <Spacer height={16} />
          <p>
            <a
              href="https://share.hsforms.com/1BVFH0zJTQ86GHcButJfjKw4fxs5"
              rel="noopener noreferrer"
              style={{ fontWeight: 600 }}
              target="_blank"
            >
              Not a Solution Partner yet? Apply here!
            </a>
          </p>
        </Fragment>
      ),
      links: [
        {
          href: 'https://partnerportal.zapier.com/wikis/help/RejectedLead',
          title: 'Why has my lead been rejected?',
        },
        {
          href:
            'https://partnerportal.zapier.com/wikis/help/RegisteringNewDeals',
          title: 'Registering New Deals',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'message',
        type: 'text',
        label: 'Describe your issue or question',
        isMultiline: true,
        isRequired: true,
      },
      {
        id: 'portal_email',
        type: 'text',
        label:
          'Provide the email address you use to log into the Partner Portal',
        isRequired: true,
      },
      {
        id: 'company_name',
        type: 'text',
        label: 'Provide your company name as it appears in the Partner Portal',
        isRequired: true,
      },
      {
        id: 'lead_email',
        type: 'text',
        label:
          'Provide the email address of the lead you want to enquire about',
        isRequired: false,
      },
      {
        id: 'custom_field_33382613130125',
        type: 'hidden',
        value: 'expsup-expert-reporting',
        zendeskCustomFieldId: 33382613130125,
      },
      {
        id: 'custom_field_33382904444941',
        type: 'hidden',
        value: 'expsup-expert-reporting-lead',
        zendeskCustomFieldId: 33382904444941,
      },
    ],
  }),
});
